import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router";

import { RadioGroup } from "capitalrx-components-2.0/lib/forms";
import { Button, Icon, Link, Spinner, Table, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold, Subheading, Subtitle, Title } from "capitalrx-components-2.0/lib/typography";

import { getPaymentPreferenceAPI, getProviderManagementAPI } from "@/api";
import { ProviderManagement } from "@/api/interfaces";
import { getDocumentsByType } from "@/components/Account/Update/components";
import { PROVIDER_MANAGEMENT_LAYOUT, PROVIDER_STATUS_VALUES, STANDARD_RESPONSIVE_LAYOUT } from "@/constants";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, getThemeURL, hexToRgbColor } from "@/utils";

import { StatusTag } from "./StatusTag";
import { PaymentRemittanceEdit } from "./PaymentRemittanceEdit";


export const PaymentRemittanceView: FC = () => {
  const { four, six } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const { primary_color } = useContext(ThemeSelectorContext);
  const { Layout } = useResponsive(STANDARD_RESPONSIVE_LAYOUT);
  const { getAuthToken } = useContext(AuthenticationContext);

  const [isEditPayment, setIsEditPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentPreference, setPaymentPreference] = useState<ProviderManagement.PaymentPreferenceSchema>();
  const [documents, setDocuments] = useState<ProviderManagement.DocumentSchema[]>();
  const [provider, setProvider] = useState<ProviderManagement.ProviderSchema>();

  const paymentType = paymentPreference?.payment_type;
  const remittanceType = paymentPreference?.remittance_type;
  const enrollmentStatus = paymentPreference?.network_enrollment_status;

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const networkName = params.get("name");
  const { providerNetworkId } = useParams();

  const NON_EDITABLE_STATUSES = [
    PROVIDER_STATUS_VALUES.NEEDS_APPROVAL,
    PROVIDER_STATUS_VALUES.DEACTIVATED,
    PROVIDER_STATUS_VALUES.PENDING,
    PROVIDER_STATUS_VALUES.PROCESSING,
    PROVIDER_STATUS_VALUES.REVIEW,
  ];

  const isEditable = !NON_EDITABLE_STATUSES.includes(paymentPreference?.network_enrollment_status as typeof NON_EDITABLE_STATUSES[number]);
  const getProvider = useCallback(async () => {
    setIsLoading(true);

    try {
      // Find provider management record and process response
      const pmResponse = await getProviderManagementAPI(getAuthToken());
      setProvider(pmResponse.provider);
      setDocuments(pmResponse.documents);

      const payloadConfig = {
        provider_id: Number(provider?.id),
        provider_network_id: Number(providerNetworkId),
      };

      const paymentPreferenceRecord = await getPaymentPreferenceAPI(payloadConfig, getAuthToken());
      setPaymentPreference(paymentPreferenceRecord);

      setIsLoading(false);

    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementGet);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getProvider();
  }, []);

  const documentsByType = useMemo(() => {
    if (!documents) return null;
    return getDocumentsByType(documents);
  }, [documents]);


  const docRows = useMemo(() => {
    if (!documentsByType) return [];

    const otherDocs = [];

    if (documentsByType.authLetter) {
      otherDocs.push({ ...documentsByType.authLetter, type: "Auth Letter" });
    }

    if (documentsByType.letterhead) {
      otherDocs.push({ ...documentsByType.letterhead, type: "Letterhead" });
    }

    return otherDocs.map((doc) => ({
      id: doc.id.toString(),
      data: {
        name: doc.file_name,
        type: doc.type,
        created_at: new Date(doc.created_at).toLocaleDateString(),
        notes: doc.notes || "—",
      },
    }));
  }, [documentsByType]);

  const docDatasource = useSimpleTableDataSource(docRows);

  const handleEditClick = () => {
    setIsEditPayment(true);
  };

  return (
    <Stack spacing="large">
      {isLoading && <Stack alignment="center"><Spinner size="large" /></Stack>}
      <Stack spacing="small">
        <Shelf spacing="mini">
          <Icon color={hexToRgbColor(primary_color)} name="arrow-left" />
          <Link to={`${getThemeURL("ACCOUNT")}?tab=payment`}>Network & Payment Preferences</Link>
          <Shelf.Spacer />
        </Shelf>
        <Layout spacing="small">
          <Title>{networkName || "--"}</Title>
          {enrollmentStatus && <StatusTag status={enrollmentStatus} paymentType={paymentType} />}
          <Layout.Spacer />
          {!isEditPayment && isEditable && <Button appearance={getThemeButton("secondary", primary_color)} icon="edit" onClick={handleEditClick}>Edit</Button>}
        </Layout>
      </Stack>
      {!isEditPayment && (
        <Stack>
          <Subtitle>Payment & Remittance</Subtitle>
          <Stack>
            <Subheading>Payment Type</Subheading>
            <RadioGroup value={paymentType} orientation="horizontal" hideLabel readOnly>
              <RadioGroup.Option value={ProviderManagement.PAYMENT_TYPE_VALUES.ACH}>Bank Account/ACH</RadioGroup.Option>
              <RadioGroup.Option value={ProviderManagement.PAYMENT_TYPE_VALUES.CHECK}>Check</RadioGroup.Option>
            </RadioGroup>
          </Stack>
          <Stack>
            {paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.ACH && (
              <Stack>
                <Subheading>Financial Information</Subheading>
                <Grid>
                  <Grid.Span columns={four}>
                    <Stack spacing="mini">
                      <Semibold>Financial Institution’s Name</Semibold>
                      <Body>{paymentPreference?.financial_institution_name || "--"}</Body>
                    </Stack>
                  </Grid.Span>
                </Grid>
                <Grid>
                  <Grid.Span columns={four}>
                    <Stack spacing="mini">
                      <Semibold>Address</Semibold>
                      {paymentPreference?.financial_institution_address_line_1 && (
                        <Stack spacing="none">
                          <Body>{paymentPreference?.financial_institution_address_line_1}</Body>
                          <Body>{paymentPreference?.financial_institution_address_line_2}</Body>
                          <Body>{paymentPreference?.financial_institution_address_city}, {paymentPreference?.financial_institution_address_state} {paymentPreference?.financial_institution_address_zip_code}</Body>
                        </Stack>
                      )}
                      {!paymentPreference?.financial_institution_address_line_1 && <Body>--</Body>}
                    </Stack>
                  </Grid.Span>
                </Grid>
                <Grid>
                  <Grid.Span columns={six}>
                    <Stack spacing="mini">
                      <Semibold>Contact First Name</Semibold>
                      <Body>{paymentPreference?.financial_institution_contact_first_name || "--"}</Body>
                    </Stack>
                  </Grid.Span>
                  <Grid.Span columns={six}>
                    <Stack spacing="mini">
                      <Semibold>Contact Last Name</Semibold>
                      <Body>{paymentPreference?.financial_institution_contact_last_name || "--"}</Body>
                    </Stack>
                  </Grid.Span>
                  <Grid.Span columns={six}>
                    <Stack spacing="mini">
                      <Semibold>Phone Number</Semibold>
                      <Body>{paymentPreference?.financial_institution_phone || "--"}</Body>
                    </Stack>
                  </Grid.Span>
                  <Grid.Span columns={six}>
                    <Stack spacing="mini">
                      <Semibold>Fax Number</Semibold>
                      <Body>{paymentPreference?.financial_institution_fax || "--"}</Body>
                    </Stack>
                  </Grid.Span>
                </Grid>
              </Stack>
            )}
            {paymentPreference?.ach_account_type && (
              <Stack>
                <Subheading>Bank Account</Subheading>
                <Stack>
                  <RadioGroup label="Type of Account" value={paymentPreference?.ach_account_type} orientation="horizontal" readOnly defaultValue="checking">
                    <RadioGroup.Option value="checking">Checking</RadioGroup.Option>
                    <RadioGroup.Option value="savings">Savings</RadioGroup.Option>
                  </RadioGroup>
                </Stack>
                <Grid>
                  <Grid.Span columns={six}>
                    <Semibold>Account Owner First Name</Semibold>
                    <Body>{paymentPreference?.account_owner_first_name || "--"}</Body>
                  </Grid.Span>
                  <Grid.Span columns={six}>
                    <Semibold>Account Owner Last Name</Semibold>
                    <Body>{paymentPreference?.account_owner_last_name || "--"}</Body>
                  </Grid.Span>
                  <Grid.Span columns={six}>
                    <Semibold>Routing Number</Semibold>
                    <Body>{paymentPreference?.ach_routing_number || "--"}</Body>
                  </Grid.Span>
                  <Grid.Span columns={six}>
                    <Semibold>Bank Account Number</Semibold>
                    <Body>{paymentPreference?.ach_bank_account_number || "--"}</Body>
                  </Grid.Span>
                </Grid>
              </Stack>
            )}
            <Stack>
              {/* This next portion is commented as per ad-hoc design detailed in https://capitalrx.atlassian.net/browse/CAP-80855 since we'll need to bring it back for VCC */}
              {/* <Subheading>Remittance Type</Subheading>
              <RadioGroup
                value={remittanceType}
                hideLabel
                orientation="horizontal"
                readOnly
              >
                <RadioGroup.Option value={ProviderManagement.REMITTANCE_TYPE_VALUES.ELECTRONIC}>Electronic</RadioGroup.Option>
                <RadioGroup.Option value={ProviderManagement.REMITTANCE_TYPE_VALUES.PAPER}>Paper</RadioGroup.Option>
              </RadioGroup> */}
              {remittanceType === ProviderManagement.REMITTANCE_TYPE_VALUES.PAPER && paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.ACH && (
                <Stack>
                  <Subheading>Remit To Address</Subheading>
                  <Grid>
                    <Grid.Span columns={four}>
                      <Stack spacing="mini">
                        <Semibold>Address</Semibold>
                        {paymentPreference?.remittance_address_line_1 && (
                          <Stack spacing="none">
                            <Body>{paymentPreference?.remittance_address_line_1}</Body>
                            <Body>{paymentPreference?.remittance_address_line_2}</Body>
                            <Body>{paymentPreference?.remittance_address_city}, {paymentPreference?.remittance_address_state} {paymentPreference?.remittance_address_zip_code}</Body>
                          </Stack>
                        )}
                        {!paymentPreference?.remittance_address_line_1 && <Body>--</Body>}
                      </Stack>
                    </Grid.Span>
                  </Grid>
                </Stack>
              )}
            </Stack>
          </Stack>
          {provider?.type === ProviderManagement.PROVIDER_TYPE_VALUES.MEDICAL && (
            <Stack>
              <Heading>Transaction Validation</Heading>
              <Stack>
                <Subheading>Claim 1</Subheading>
                <Grid>
                  <Grid.Span columns={six}>
                    <Stack spacing="mini">
                      <Semibold>Claim Number</Semibold>
                      <Body>{paymentPreference?.transaction_validation_claim_1_number || "--"}</Body>
                    </Stack>
                  </Grid.Span>
                  <Grid.Span columns={six}>
                    <Stack spacing="mini">
                      <Semibold>Paid Amount</Semibold>
                      <Body>{paymentPreference?.transaction_validation_claim_1_paid_amount || "--"}</Body>
                    </Stack>
                  </Grid.Span>
                </Grid>
              </Stack>
              <Stack>
                <Subheading>Claim 2</Subheading>
                <Grid>
                  <Grid.Span columns={six}>
                    <Stack spacing="mini">
                      <Semibold>Claim Number</Semibold>
                      <Body>{paymentPreference?.transaction_validation_claim_2_number || "--"}</Body>
                    </Stack>
                  </Grid.Span>
                  <Grid.Span columns={six}>
                    <Stack spacing="mini">
                      <Semibold>Paid Amount</Semibold>
                      <Body>{paymentPreference?.transaction_validation_claim_2_paid_amount || "--"}</Body>
                    </Stack>
                  </Grid.Span>
                </Grid>
              </Stack>
            </Stack>
          )}
          <Stack>
            <Heading>Documents</Heading>
            {documents && (
              <Table dataSource={docDatasource}>
                <Table.Text name="type" title="Type" initialWidth="span" />
                <Table.Text name="name" title="Name" initialWidth="span" />
                <Table.Text name="created_at" title="Date Added" />
                <Table.Text name="notes" title="Notes" initialWidth="span" />
              </Table>
            )}
          </Stack>
        </Stack>
      )}
      {isEditPayment && (
        <PaymentRemittanceEdit provider={provider} setProvider={setProvider} documents={documents} paymentPreference={paymentPreference} setPaymentPreference={setPaymentPreference} setIsEditPayment={setIsEditPayment} />
      )}
    </Stack>
  );
};
